import { datadogRum } from '@datadog/browser-rum'
import type { AssertiveYieldManagerEnv } from '../../types/assertive'
import { withAyManager } from '../../utils/ad-ops/assertive-yield/queue'
import { slotState } from './slots'

// When an app-owned programmatic slot auctions and renders. See
// docs/adops/ASSERTIVE-YIELD.md, "Slot timing".

export const DISPLAY_MARGIN_FLAG = 'adDisplayMargin'
export const RENDER_MARGIN_FLAG = 'adRenderMargin'

/** How far below the viewport the app displays, then renders, a slot. */
export type SlotMargins = { displayPx: number; renderPx: number }

export const VIEWPORT_EDGE: SlotMargins = { displayPx: 0, renderPx: 0 }

const MANUAL_EVENT = 'Manual event'

/** A flag value as a margin in px. Anything unusable means the viewport edge. */
export function marginPx(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : 0
}

type EnvSettings = Pick<AssertiveYieldManagerEnv, 'settings'> | undefined

/**
 * The app owns a placement whose AY row auctions on display() and leaves the
 * GAM request to the manual event, i.e. to the app's render(). Any other row,
 * or none, keeps AY in charge.
 */
export function appDrivesPlacement(
  env: EnvSettings,
  placement: string
): boolean {
  const row = env?.settings?.placements?.find((p) => p.code === placement)
  if (!row || row.lazyLoad || row.lazyRender) return false

  const triggers = env?.settings?.definitions?.triggers ?? {}
  const isManual = (id: string) => triggers[id]?.event === MANUAL_EVENT
  const fetchIds = row.fetchTrigger ?? []
  const renderIds = row.trigger ?? []
  return (
    fetchIds.length > 0 &&
    fetchIds.every((id) => Boolean(triggers[id]) && !isManual(id)) &&
    renderIds.length > 0 &&
    renderIds.every(isManual)
  )
}

export function anyAppOwnedPlacement(env: EnvSettings): boolean {
  return (env?.settings?.placements ?? []).some((p) =>
    appDrivesPlacement(env, p.code)
  )
}

// RUM vitals carry the arm a page ran, which the cohort alone can't tell once
// ConfigCat rules change, a query overrides the flags or they fail to load.
export function tagRumWithMargins(margins: SlotMargins): void {
  datadogRum.setGlobalContextProperty('ad_display_margin', margins.displayPx)
  datadogRum.setGlobalContextProperty('ad_render_margin', margins.renderPx)
}

// The margins only apply once AY hands a placement's render to the app.
export function tagRumWithAppOwned(env: EnvSettings): void {
  datadogRum.setGlobalContextProperty('ad_app_owned', anyAppOwnedPlacement(env))
}

export function tagRumWithAyVersion(
  env: Pick<AssertiveYieldManagerEnv, 'versionInfo'> | undefined
): void {
  const versionId = env?.versionInfo?.versionId
  if (versionId === undefined) return
  datadogRum.setGlobalContextProperty('ay_version', versionId)
}

/** Runs `fn` once the element is within `marginPx` of the viewport. */
export function whenWithin(
  element: Element,
  marginPx: number,
  fn: () => void
): () => void {
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      fn()
    },
    { rootMargin: `${marginPx}px 0px ${marginPx}px 0px` }
  )
  observer.observe(element)
  return () => observer.disconnect()
}

export type SlotDriver = {
  /** AY's auction defined the GPT slot, renamed to this instance code. */
  defined: (instanceCode: string) => void
  stop: () => void
}

/**
 * Renders a displayed, app-owned slot once AY has defined it and it is within
 * `renderPx`. AY ignores render() before that, under the pre-rename code and
 * inside its afterDefineSlot callback, hence the defined code and the deferral.
 * It never repeats a GAM request AY made itself, so the slot registry must be
 * listening before display().
 */
export function driveSlot(
  container: HTMLElement,
  renderPx: number,
  env: Pick<AssertiveYieldManagerEnv, 'render'>
): SlotDriver {
  let stopped = false
  let inRange = false
  let definedCode = ''
  let rendered = false

  function maybeRender(): void {
    if (stopped || rendered || !inRange || !definedCode) return
    rendered = true
    const code = definedCode
    setTimeout(() => {
      withAyManager(() => {
        if (stopped || slotState(code).requested) return
        env.render([code], { useInstanceCodes: true })
      })
    }, 0)
  }

  const disconnect = whenWithin(container, renderPx, () => {
    inRange = true
    maybeRender()
  })

  return {
    defined(instanceCode) {
      definedCode = instanceCode
      maybeRender()
    },
    stop() {
      stopped = true
      disconnect()
    },
  }
}
